import { gql } from "@apollo/client";
import type {
  DependencySet,
  DependencyAiAnalyst,
  DependencyAutomation,
  DependencyRule,
  DependencyWidget,
} from "../../AppView/Dependencies/types";
import type { SavedFilterType } from "../FindingsUtils";

export const GET_FINDING_FILTERS = gql`
  query filters($filterType: FindingFilterTypeEnum!) {
    finding_filters(filters: { visible: true, filter_type: $filterType }) {
      edges {
        node {
          name
          filter_data
          filter_type
          id
          editable
          created_by
          system
          popular
          default
        }
        cursor
      }
      pageInfo {
        hasNextPage
        startCursor
      }
    }
  }
`;

export interface GetFindingFiltersVars {
  filterType: SavedFilterType;
}

export const GET_SCOPES = gql`
  query scopes($include_unscoped: Boolean) {
    scope_hierarchy(include_unscoped: $include_unscoped) {
      scope_hierarchy
    }
  }
`;

export const GET_FILTERS_DATA = gql`
  query getFiltersData(
    $filterType: FindingFilterTypeEnum!
    $context: FindingFiltersDataContext
  ) {
    finding_filters_data(filter_type: $filterType, context: $context) {
      field
      conditions
      type
      visible_to_all
      display_options
      category
      display_name
      is_searchable
      related_datasources
    }
  }
`;

export const SAVE_FILTER = gql`
  mutation SaveFilter(
    $name: String!
    $filterData: JSONString!
    $filterType: FindingFilterTypeEnum!
    $scope_group: String
  ) {
    new_finding_filter_v2(
      finding_filter: {
        name: $name
        filter_data: $filterData
        filter_type: $filterType
        scope_group: $scope_group
      }
    ) {
      finding_filter {
        name
        filter_data
        id
      }
    }
  }
`;

export interface SavedFilter {
  name: string;
  filter_data: string;
  id: string;
}

export interface SaveFilterResponse {
  new_finding_filter_v2: {
    finding_filter: SavedFilter | null;
  };
}

export interface SaveFilterVars {
  name: string;
  filterData: string;
  filterType: SavedFilterType;
  scope_group?: string;
}

export const SAVE_SCOPE = gql`
  mutation SaveScope(
    $name: String!
    $filterData: JSONString!
    $filterType: FindingFilterTypeEnum!
    $scope_group: String
  ) {
    new_finding_scope_v2(
      finding_filter: {
        name: $name
        filter_data: $filterData
        filter_type: $filterType
        scope_group: $scope_group
      }
    ) {
      finding_filter {
        name
        filter_data
        id
      }
    }
  }
`;

export const UPDATE_SCOPE = gql`
  mutation UpdateScope(
    $name: String!
    $filterData: JSONString!
    $filterType: FindingFilterTypeEnum!
    $id: String!
    $scope_group: String
    $overwrite: Boolean
  ) {
    update_finding_scope_v2(
      finding_filter: {
        name: $name
        filter_data: $filterData
        filter_type: $filterType
        scope_group: $scope_group
      }
      finding_filter_id: $id
      overwrite: $overwrite
    ) {
      ok
      effected_queues
    }
  }
`;

const FILTER_DEPENDENCIES_FRAGMENT = gql`
  fragment FilterDependenciesFields on FilterDependenciesType {
    total_count
    rules {
      id
      name
      kind
      enabled
    }
    automations {
      id
      name
      status
      state
    }
    ai_analysts {
      id
      name
      status
      state
      entity_id
    }
    widgets {
      id
      name
      dashboard_name
    }
    hidden_widgets_count
    hidden_dashboards_count
  }
`;

export const DELETE_FILTER = gql`
  ${FILTER_DEPENDENCIES_FRAGMENT}
  mutation DeleteFilter($id: ID!) {
    delete_finding_filter_v3(finding_filter_id: $id) {
      ok
      dependencies {
        ...FilterDependenciesFields
      }
    }
  }
`;

export type {
  DependencySet,
  DependencyAiAnalyst,
  DependencyAutomation,
  DependencyRule,
  DependencyWidget,
};

export interface DeleteFilterResponse {
  delete_finding_filter_v3: {
    ok: boolean;
    dependencies: DependencySet | null;
  };
}

export const UPDATE_FILTER_V3 = gql`
  ${FILTER_DEPENDENCIES_FRAGMENT}
  mutation UpdateFilterV3(
    $id: ID!
    $name: String!
    $filterData: JSONString!
    $filterType: FindingFilterTypeEnum!
    $confirmed: Boolean
  ) {
    update_finding_filter_v3(
      finding_filter_id: $id
      finding_filter: {
        name: $name
        filter_data: $filterData
        filter_type: $filterType
      }
      confirmed: $confirmed
    ) {
      ok
      dependencies {
        ...FilterDependenciesFields
      }
    }
  }
`;

export interface UpdateFilterResponse {
  update_finding_filter_v3: {
    ok: boolean;
    // Populated only when a criteria change was blocked (ok === false).
    dependencies: DependencySet | null;
  };
}

export interface UpdateFilterVars {
  id: string;
  name: string;
  filterData: string;
  filterType: SavedFilterType;
  confirmed: boolean;
}

export const FINDING_FILTER_DEPENDENCIES = gql`
  ${FILTER_DEPENDENCIES_FRAGMENT}
  query findingFilterDependencies($id: ID!) {
    finding_filter_dependencies(finding_filter_id: $id) {
      ...FilterDependenciesFields
    }
  }
`;

export interface FilterDependenciesQueryResponse {
  finding_filter_dependencies: DependencySet | null;
}

export const GET_FILTER_ROW_VALUES = gql`
  query getFilterRowValues(
    $filterName: String!
    $contains: String
    $limit: Int
    $entity: String
    $with_total_count: Boolean = false
  ) {
    finding_filter_data(
      filter_name: $filterName
      contains: $contains
      limit: $limit
      entity: $entity
    ) {
      values
      type
      conditions
      field
      total_count @include(if: $with_total_count)
    }
  }
`;

export const SUGGEST_AI_FILTER_TITLE = gql`
  query suggest_ai_filter_title(
    $filter_type: String!
    $filter_data: JSONString!
  ) {
    suggest_ai_filter_title(
      filter_type: $filter_type
      filter_data: $filter_data
    ) {
      filter_title
    }
  }
`;
